import { useState } from "react";
import LayersIcon from "@mui/icons-material/Layers";
import CheckIcon from "@mui/icons-material/Check";
import basemaps from "./BaseMaps";
/**
 * BasemapGallery
 *
 * Styled to match ToolBox — same button style, same panel header style.
 * Positioned bottom-left inside the map div as an absolute sibling.
 *
 * @param {Array}    basemaps   - Array of { id, name, url }
 * @param {string}   selectedId - Currently active basemap id
 * @param {Function} onSelect   - Called with basemap object on selection
 */
export default function BasemapGallery({ selectedId, onSelect }) {
  const [open, setOpen] = useState(false);

  const getThumbnail = (url) =>
    url.replace("{z}", "0").replace("{x}", "0").replace("{y}", "0");

  return (
    <div style={{ position: "relative", display: "inline-block" }}>

      {/* ── Icon button — always visible, toggles panel ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Basemap"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, padding: 0,
          border: `1px solid ${open ? "#1976d2" : "rgba(0,0,0,0.23)"}`,
          borderRadius: 4,
          background: open ? "rgba(25,118,210,0.08)" : "#fff",
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          transition: "all 0.15s ease",
          color: open ? "#1976d2" : "#555",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = open ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.04)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = open ? "rgba(25,118,210,0.08)" : "#fff"; }}
      >
        <LayersIcon style={{ fontSize: 22, color: open ? "#1976d2" : "#555" }} />
      </button>

      {/* ── Panel — matches ToolBox panel style ── */}
      {open && (
        <div style={{
          position: "absolute",
          top: 0,
          right: "58px",
          width: 220,
          background: "#fff",
          borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          zIndex: 1300,
          overflow: "hidden",
        }}>

          {/* Header — matches ToolBox AppBar */}
          <div style={{
            display: "flex", alignItems: "center",
            padding: "0 6px 0 16px",
            background: "#1976d2", minHeight: 44, flexShrink: 0,
          }}>
            <span style={{
              fontFamily: "Roboto, Arial, sans-serif",
              fontSize: 15, fontWeight: 500, color: "#fff", flex: 1,
            }}>
              Basemap
            </span>
            <button
              onClick={() => setOpen(false)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, border: "none", borderRadius: 4,
                background: "transparent", cursor: "pointer", color: "#fff",
                fontSize: 18, lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>

          {/* Thumbnail grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 6,
            padding: 8,
            overflowY: "auto",
            maxHeight: 300,
          }}>
            {basemaps.map((bm) => {
              const isSelected = bm.id === selectedId;
              return (
                <div
                  key={bm.id}
                  onClick={() => { onSelect?.(bm); setOpen(false); }}
                  title={bm.name}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    borderRadius: 4,
                    overflow: "hidden",
                    outline: isSelected ? "2px solid #1976d2" : "2px solid transparent",
                    outlineOffset: 1,
                    transition: "outline 0.1s, transform 0.1s",
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.outline = "2px solid #90caf9"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.outline = "2px solid transparent"; }}
                >
                  {/* Thumbnail img */}
                  <img
                    src={getThumbnail(bm.url)}
                    alt={bm.name}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                    style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }}
                  />

                  {/* Fallback */}
                  <div style={{
                    display: "none", width: "100%", aspectRatio: "1/1",
                    background: "#f0f0f0", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: 8, color: "#aaa" }}>N/A</span>
                  </div>

                  {/* Label */}
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                    padding: "3px 4px 3px",
                  }}>
                    <span style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontSize: 8, color: "#fff", fontWeight: 500,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis", display: "block",
                    }}>
                      {bm.name}
                    </span>
                  </div>

                  {/* Selected check badge */}
                  {isSelected && (
                    <div style={{
                      position: "absolute", top: 2, right: 2,
                      width: 14, height: 14, borderRadius: "50%",
                      background: "#1976d2",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <CheckIcon style={{ fontSize: 9, color: "#fff" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
}